import { getUID } from '@redviking/argonaut-util/src/getUID';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import type { UiNodeSpec } from './UiNodeSpec';
import type { UiFlowNodeInput } from '../../sockets/UiFlowNodeInput';
import type { MaterialModelNodeControlData } from '../../controls/SelectMaterialModelControl.vue';
import type { RequiredSockets } from './UiNodeSpec';
import type { NodeSocketKindMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';

export const createMaterialUiNodeSpec: UiNodeSpec<'createMaterial'> = {
    requiredSockets: (node, availableInputSockets) => {
        const ret: ReturnType<RequiredSockets<'createMaterial', NodeSocketKindMap['createMaterial']>> = {
            required: [ 'trigger' ],
            conditionallyRequired: [],
        };
        if (node.controlValues.snCfg.kind === 'direct') {
            ret.required.push('sn');
        }
        if (node.controlValues.snCfg.kind === 'formatter') {
            ret.required.push(...availableInputSockets.filter(socketName => socketName.startsWith('snInput_')));
        }
        return ret;
    },
    cfg: () => {
        const ret: NodeCfg<'createMaterial'> = {
            disabled: false,
            controlValues: {
                location: { id: '' },
                materialModelId: '',
                snCfg: { kind: 'direct' },
                udfs: {},
            },
            id: getUID(),
            kind: 'createMaterial',
        };
        return ret;
    },
    controls: getNodeCfg => ({
        location: {
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.location,
            cfg: {
                kind: 'location',
            },
        },
        materialModelId: {
            cfg: {
                kind: 'materialModelSelect',
                label: 'Material Model',
                getValue: nodeCfg => {
                    if (nodeCfg.kind !== 'createMaterial') {
                        return null;
                    }
                    return nodeCfg.controlValues.materialModelId || null;
                },
                onSelect: (materialModel: MaterialModelNodeControlData) => {
                    const nodeCfg = getNodeCfg();
                    const snFormatCfg = materialModel.materialModelSnCfg || materialModel.materialClassSnCfg;
                    const controlValues: NodeCfg<'createMaterial'>['controlValues'] = {
                        ...nodeCfg.controlValues,
                        materialModelId: materialModel.materialModelId,
                        snCfg: snFormatCfg
                            ? {
                                fmtInputs: snFormatCfg.tokens.filter(token => token.type === 'input').map(token => token.name),
                                kind: 'formatter',
                            }
                            : {
                                kind: 'direct',
                            },
                        udfs: materialModel.materialClassUdfSchemas.reduce((acc, schema) => {
                            const udfData: NodeCfg<'createMaterial'>['controlValues']['udfs'][string] = {
                                name: schema.name,
                                required: schema.validation_required,
                                type: schema.data_type,
                            };
                            acc[schema.id] = udfData;
                            return acc;
                        }, {} as NodeCfg<'createMaterial'>['controlValues']['udfs']),
                    };
                    createNodeSocket({
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues,
                        },
                    });
                },
            },
            nodeCfg: getNodeCfg(),
            // placeholder: 'Select a material model',
            value: getNodeCfg().controlValues.materialModelId,
        },
    }),
    suppressNode: () => !accessor.testFeatureFlagRequirements([ 'unstableWorkOrders' ]), // this shouldn't be needed, but we need to conditionally enable the WO socket first
    // extraComponent: () => import('./inspector/CreateMaterial.helper.vue').then(m => m.default),
    inputs: nodeCfg => {
        const triggerInput: UiFlowNodeInput.Opts<'event'> = { socket: 'event', label: 'Trigger' };
        const workOrderIdInput: UiFlowNodeInput.Opts<'string'> = { socket: 'string', label: 'Work Order ID' };

        const udfInputs: Record<`udfInput_${string}`, UiFlowNodeInput.Opts<'varValue' | 'string'>> = {};
        for (const [ udfId, udfData ] of Object.entries(nodeCfg.controlValues.udfs)) {
            // FUTURE: udfData has a `required` boolean that we can use to style the optional udf inputs
            udfInputs[`udfInput_${udfId}`] = { socket: udfData.type === 'number' ? 'varValue' : 'string', label: `${nodeCfg.controlValues.udfs[udfId].name} - UDF Input` };
        }

        if (nodeCfg.controlValues.snCfg.kind === 'direct') {
            return {
                trigger: triggerInput,
                workOrderId: workOrderIdInput,
                sn: { socket: 'string', label: 'Serial Number' },
                ...udfInputs,
            };
        } else {
            const snInputs: Record<`snInput_${string}`, UiFlowNodeInput.Opts<'varValue' | 'string'>> = {};
            for (const fmtInput of nodeCfg.controlValues.snCfg.fmtInputs) {
                snInputs[`snInput_${fmtInput}`] = { socket: 'string', label: `${fmtInput} - Serial Number Input` };
            }
            return {
                trigger: triggerInput,
                workOrderId: workOrderIdInput,
                sn: { socket: 'string', label: 'Serial Number' },
                ...snInputs,
                ...udfInputs,
            };
        }
    },
    kindLabel: 'Create Material',
    nodeCategory: 'argonautApi',
    outputs: () => ({
        next: { socket: 'event', label: 'Next' },
        materialId: { socket: 'string', label: 'Material ID' },
        serialNumber: { socket: 'string', label: 'Serial Number' },
    }),
    validator: node => {
        const errors: Record<string, string> = {};
        if ('deployParamKind' in node.controlValues.location) {
            if (!node.controlValues.location.deployParamId) {
                errors.location = 'Location deployment parameter is required';
            }
        } else if (!node.controlValues.location.id) {
            errors.location = 'Location is required';
        }
        if (!node.controlValues.materialModelId) {
            errors.materialModelId = 'Material Model is required';
        }
        /**
         * FUTURE: This should be a warning not an error that prevents save.
         * This loop throw an error if a udf input is not present, but the default could be set at the material model.
         * Today if neither are supplied the plv8 function will error.
         */
        // for (const udfId of Object.keys(node.controlValues.udfs)) {
        //     if (!Object.values(flow.connections).find(c => c.targetNodeId === node.id && c.targetNodePort === `udfInput_${udfId}`)) {
        //         errors[`udfInput_${udfId}`] = 'UDF input is required';
        //     }
        // }
        return Object.keys(errors).length ? errors : true;
    },
};
